import { z } from 'zod';

import { JSONSchema, PrimaryKeySchema, commaDelimitedList } from './utils';

// Thought schema for agent execution thoughts
export const AgentExecutionThoughtSchema = z.object({
  text: z.string().describe('The thought content'),
  createdAt: z.coerce.date().describe('When the thought was created'),
});

export type AgentExecutionThought = z.infer<typeof AgentExecutionThoughtSchema>;

// Status enum for agent executions
export const AgentExecutionStatusSchema = z.enum([
  'pending',
  'running',
  'completed',
  'failed',
  'cancelled',
  'paused',
]);

export type AgentExecutionStatus = z.infer<typeof AgentExecutionStatusSchema>;

/**
 * Schema for agent executions which store the configurable execution of agents
 */
export const AgentExecutionSchema = z.object({
  agentExecutionId: PrimaryKeySchema,
  agentConfigurationVersionId: PrimaryKeySchema,

  // Execution inputs and outputs
  agentInput: z
    .record(z.any())
    .describe('The input data provided to the agent'),
  agentInputSchema: JSONSchema.describe(
    'JSON Schema that validates the agent input',
  ),
  agentInputSummary: z.string().describe('Human-readable summary of the input'),
  agentOutput: z
    .record(z.any())
    .nullable()
    .describe('The output produced by the agent'),
  agentOutputSchema: JSONSchema.describe(
    'JSON Schema that validates the agent output',
  ),
  agentOutputSummary: z
    .string()
    .nullable()
    .describe('Human-readable summary of the output'),

  // Execution state
  status: AgentExecutionStatusSchema,
  thoughts: z
    .array(AgentExecutionThoughtSchema)
    .describe('Agent reasoning thoughts during execution'),

  // Execution configuration
  maxActions: z
    .number()
    .int()
    .positive()
    .nullable()
    .describe('Maximum actions for this execution'),
  currentStep: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .describe('Current execution step'),
  planningInterval: z
    .number()
    .int()
    .positive()
    .nullable()
    .describe('Steps between replanning'),

  // Timestamps
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  failedAt: z.coerce.date().nullable(),
  cancelledAt: z.coerce.date().nullable(),
  pausedAt: z.coerce.date().nullable(),
});

export type AgentExecution = z.infer<typeof AgentExecutionSchema>;

export const AgentExecutionsSchema = z.array(AgentExecutionSchema);

export type AgentExecutions = z.infer<typeof AgentExecutionsSchema>;

export const CreateAgentExecutionSchema = AgentExecutionSchema.pick({
  agentConfigurationVersionId: true,
  agentInput: true,
  agentInputSchema: true,
  agentInputSummary: true,
  maxActions: true,
  planningInterval: true,
  agentOutputSchema: true,
});

export type CreateAgentExecution = z.infer<typeof CreateAgentExecutionSchema>;

export const UpdateAgentExecutionSchema = z.object({
  agentOutput: z.record(z.any()).optional(),
  agentOutputSchema: JSONSchema.optional(),
  agentOutputSummary: z.string().optional(),
  status: AgentExecutionStatusSchema.optional(),
  thoughts: z.array(AgentExecutionThoughtSchema).optional(),
  currentStep: z.number().int().nonnegative().optional(),
  startedAt: z.coerce.date().optional(),
  completedAt: z.coerce.date().optional(),
  failedAt: z.coerce.date().optional(),
  cancelledAt: z.coerce.date().optional(),
  pausedAt: z.coerce.date().optional(),
});

export type UpdateAgentExecution = z.infer<typeof UpdateAgentExecutionSchema>;

export const AgentExecutionsQueryOptionsSchema = z.object({
  agentExecutionIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  agentIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  teamIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  agentConfigurationVersionIds: commaDelimitedList(
    PrimaryKeySchema.array(),
  ).optional(),
  statuses: commaDelimitedList(AgentExecutionStatusSchema.array()).optional(),
  hasAgentOutput: z.boolean().optional(),
});

export type AgentExecutionsQueryOptions = z.infer<
  typeof AgentExecutionsQueryOptionsSchema
>;
